import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { RealtimeChannel } from '@supabase/supabase-js';
import { Subject, takeUntil } from 'rxjs';
import { IstDatePipe } from '../../../pipes/ist-date.pipe';
import { SnackbarService } from '../../../services/design/snackbar.service';
import {
  AdminOrder,
  OrderStatus,
  OrdersService,
} from '../../../services/orders/orders.service';
import {
  StoreOption,
  StoreService,
} from '../../../services/stores/store.service';
import { AuthService } from '../../../services/auth/auth.service';
import { Rider, RiderService } from '../../../services/riders/rider.service';

import { OrderBillSummaryComponent } from '../components/order-bill-summary.component';
import { NotificationService } from '../../../services/notifications/notification.service';
import { PackingTaskComponent } from '../../operations/packing-task.component';

@Component({
  selector: 'app-all-orders',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, IstDatePipe, OrderBillSummaryComponent, PackingTaskComponent],
  templateUrl: './all-orders.component.html',
  styleUrls: ['./all-orders.component.scss'],
})
export class AllOrdersComponent implements OnInit, OnDestroy {
  private ordersService = inject(OrdersService);
  private snackbar = inject(SnackbarService);
  private storeService = inject(StoreService);
  private authService = inject(AuthService);
  private riderService = inject(RiderService);
  private router = inject(Router);
  private recovery = inject(NotificationService);
  private requestVersion = 0;

  orders = signal<AdminOrder[]>([]);
  selectedOrder = signal<AdminOrder | null>(null);
  isLoadingOrderDetails = signal(false);
  manualAssignmentOrder = signal<AdminOrder | null>(null);
  availableRiders = signal<Rider[]>([]);
  selectedManualRiderId = signal<string | null>(null);
  totalOrders = signal<number>(0);
  isLoading = signal<boolean>(false);
  isAssigningRider = signal<boolean>(false);
  private destroy$ = new Subject<void>();
  private realtimeSubscription?: RealtimeChannel;
  private realtimeRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  // Stores
  stores = signal<StoreOption[]>([]);
  selectedStoreId = signal<string | null>(null);
  isStoreManager = signal<boolean>(false);

  // Filters
  currentPage = 1;
  pageSize = 10;
  searchTerm = '';
  activeStatusTab: OrderStatus | 'ALL' = 'ALL';
  paymentStatusFilter: string | 'ALL' = 'ALL';
  fromDate = signal<string | null>(null);
  toDate = signal<string | null>(null);

  readonly statusTabs: (OrderStatus | 'ALL')[] = [
    'ALL',
    'PENDING',
    'CONFIRMED',
    'PREPARING',
    'READY_FOR_PICKUP',
    'RIDER_ASSIGNED',
    'RIDER_ACCEPTED',
    'RIDER_AT_STORE',
    'DISPATCHED',
    'DELIVERED',
    'CANCELLED',
  ];

  ngOnInit(): void {
    this.recovery.recovered.pipe(takeUntil(this.destroy$)).subscribe(() => this.scheduleRealtimeRefresh());
    this.loadStores();
  }

  loadStores() {
    const role = this.authService.role();
    const assignedStoreId = this.authService.storeId();

    if (role === 'store_manager' && assignedStoreId) {
      this.isStoreManager.set(true);
      this.selectedStoreId.set(assignedStoreId);
    }

    this.storeService.allStores().subscribe({
      next: (res) => {
        if (this.isStoreManager()) {
          this.stores.set(res.filter(s => s.id.toString() === assignedStoreId));
        } else {
          this.stores.set(res);
        }

        this.loadOrders();
        this.loadRiders();
        this.setupRealtime();
      },
      error: () => this.snackbar.error('Could not load stores'),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.realtimeSubscription) {
      this.realtimeSubscription.unsubscribe();
    }
    if (this.realtimeRefreshTimer) {
      clearTimeout(this.realtimeRefreshTimer);
    }
  }

  setupRealtime() {
    if (this.realtimeSubscription) {
      this.realtimeSubscription.unsubscribe();
    }
    this.realtimeSubscription = this.ordersService.subscribeToOrders(
      () => this.scheduleRealtimeRefresh(),
      this.selectedStoreId()
    );
  }

  private scheduleRealtimeRefresh(): void {
    if (this.realtimeRefreshTimer) return;

    this.realtimeRefreshTimer = setTimeout(() => {
      this.realtimeRefreshTimer = null;
      this.loadOrders();
    }, 1000);
  }

  onStoreChange(storeId: string | number | null): void {
    this.selectedStoreId.set(storeId === null ? null : String(storeId));
    this.currentPage = 1;
    this.loadOrders();
    this.loadRiders();
    this.setupRealtime();
  }

  loadOrders() {
    const version = ++this.requestVersion;
    this.isLoading.set(true);
    const status =
      this.activeStatusTab === 'ALL' ? undefined : this.activeStatusTab;

    const storeId = this.selectedStoreId();
    if (!storeId && this.isStoreManager()) {
      this.isLoading.set(false);
      return;
    }

    this.ordersService
      .getStoreAdminOrdersRPC({
        target_store_id: storeId,
        filter_status: status?.toLowerCase(),
        filter_payment_status:
          this.paymentStatusFilter === 'ALL'
            ? undefined
            : this.paymentStatusFilter.toLowerCase(),
        from_date: this.fromDate() || undefined,
        to_date: this.toDate() || undefined,
        search: this.searchTerm,
        limit_count: this.pageSize,
        offset_count: (this.currentPage - 1) * this.pageSize,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (version !== this.requestVersion) return;
          // Note: RPC doesn't return count directly in this mode,
          // we might need a separate count query or update RPC to return a wrapper
          this.orders.set(res.data);
          if (res.data.length < this.pageSize) {
            this.totalOrders.set(res.total);
          } else {
            // Exact total not returned by RPC yet — show at-least count for paging
            this.totalOrders.set(res.total);
          }
          this.isLoading.set(false);
        },
        error: (err) => {
          if (version !== this.requestVersion) return;
          console.error(err);
          this.snackbar.error('Failed to load live orders');
          this.isLoading.set(false);
        },
      });
  }

  loadRiders() {
    const storeId = this.selectedStoreId();
    this.riderService
      .getAssignableRiders(storeId ? Number(storeId) : undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => this.availableRiders.set(res),
        error: () => this.snackbar.error('Could not load riders'),
      });
  }

  changeStatusTab(status: OrderStatus | 'ALL') {
    this.activeStatusTab = status;
    this.currentPage = 1;
    this.loadOrders();
  }

  updateStatus(orderId: number, status: OrderStatus, selectedOrder?: any) {
    // Physical confirmations live together in Order Details.
    if (status === 'PREPARING' || status === 'DISPATCHED') {
      this.openOrderDetailsFull(orderId);
      return;
    }

    // Mark ready → compute distance (with fallback) → ready_for_pickup + auto-assign
    if (status === 'READY_FOR_PICKUP' && selectedOrder) {
      this.markReadyAndAutoAssign(orderId, selectedOrder);
      return;
    }

    this.executeChangeStatus(orderId, status);
  }

  private markReadyAndAutoAssign(orderId: number, selectedOrder: any) {
    if (['CONFIRMED', 'PREPARING'].includes(selectedOrder.current_status)) {
      this.openOrderDetailsFull(orderId);
      return;
    }
    this.isLoading.set(true);

    const finishReady = (distanceKm: number, estimatedMinutes: number) => {
      this.ordersService
        .markOrderReadyForPickup(orderId, {
          distanceKm,
          estimatedMinutes,
        })
        .subscribe({
          next: () => {
            this.snackbar.success(
              `Order #${orderId} ready — rider auto-assigned`
            );
            this.loadOrders();
            this.isLoading.set(false);
          },
          error: (err) => {
            console.error('Failed to mark ready / assign:', err);
            if (this.isNoAvailableRiderError(err)) {
              this.openManualAssignment(selectedOrder);
              this.snackbar.warning(
                'No available rider found. Assign a rider manually.'
              );
            } else {
              this.snackbar.error(
                err?.message || 'Failed to mark order ready'
              );
            }
            this.isLoading.set(false);
          },
        });
    };

    const route = this.ordersService.resolveOrderRoutePoints(selectedOrder);
    if (!route) {
      console.warn(
        '[Orders] Missing store/delivery coordinates — using default distance'
      );
      finishReady(2, 15);
      return;
    }

    this.ordersService
      .getDistanceAndDuration(route.origin, route.destination)
      .subscribe({
        next: (res) => {
          finishReady(res.distanceMeters / 1000, res.durationSeconds / 60);
        },
        error: (err) => {
          console.error('Failed to calculate distance:', err);
          const fallback = this.ordersService.estimateRouteFallback(
            route.origin,
            route.destination
          );
          this.snackbar.warning(
            'Google distance unavailable — using estimated distance'
          );
          finishReady(fallback.distanceKm, fallback.estimatedMinutes);
        },
      });
  }

  private executeChangeStatus(orderId: number, status: OrderStatus) {
    if (status === 'DELIVERED') {
      this.router.navigate(['/orders/detail', orderId]);
      return;
    }

    this.isLoading.set(true);
    this.ordersService.updateOrderStatus(orderId, status).subscribe({
      next: () => {
        this.snackbar.success(`Order #${orderId} updated to ${status}`);
        this.loadOrders();
        const selected = this.selectedOrder();
        if (selected?.order_id === orderId) {
          this.selectedOrder.set({
            ...selected,
            current_status: status,
            status: status.toLowerCase(),
            priority:
              status === 'PENDING' || status === 'CONFIRMED'
                ? 'HIGH'
                : status === 'PREPARING'
                ? 'MEDIUM'
                : 'LOW',
            action_required: this.getActionLabel(status),
          });
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Status update failed:', err);
        this.snackbar.error(err?.message || 'Status update failed');
        this.isLoading.set(false);
      },
    });
  }

  showOrder(order: AdminOrder) {
    this.selectedOrder.set(order);
    this.isLoadingOrderDetails.set(true);

    this.ordersService
      .getStoreAdminOrderDetails(order.order_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (details) => {
          if (this.selectedOrder()?.order_id === order.order_id) {
            this.selectedOrder.set(details);
          }
          this.isLoadingOrderDetails.set(false);
        },
        error: (err) => {
          console.error('Failed to load complete order details:', err);
          this.snackbar.error('Could not load complete order details');
          this.isLoadingOrderDetails.set(false);
        },
      });
  }

  closeOrderDetails() {
    this.selectedOrder.set(null);
    this.isLoadingOrderDetails.set(false);
  }

  openOrderDetailsFull(orderId: number) {
    this.closeOrderDetails();
    this.router.navigate(['/orders/detail', orderId]);
  }

  openManualAssignment(order: AdminOrder) {
    if (order.current_status !== 'READY_FOR_PICKUP') {
      this.openOrderDetailsFull(order.order_id);
      return;
    }
    this.manualAssignmentOrder.set(order);
    this.selectedManualRiderId.set(null);
    if (this.availableRiders().length === 0) {
      this.loadRiders();
    }
  }

  closeManualAssignment() {
    this.manualAssignmentOrder.set(null);
    this.selectedManualRiderId.set(null);
  }

  manualRidersForOrder(order: AdminOrder): Rider[] {
    const storeId = String(
      order.store?.store_id || this.selectedStoreId() || ''
    );

    return this.availableRiders().filter((rider) => {
      const isApproved =
        String(rider.verification_status || '').toLowerCase() === 'approved';
      const isSameStore = !storeId || String(rider.store_id) === storeId;
      return isApproved && isSameStore;
    });
  }

  canManualAssignRider(order: AdminOrder): boolean {
    if (order.rider_id) {
      return false;
    }

    return ['CONFIRMED', 'PREPARING', 'READY_FOR_PICKUP'].includes(
      order.current_status
    );
  }

  assignManualRider() {
    const order = this.manualAssignmentOrder();
    const riderId = this.selectedManualRiderId();
    if (!order || !riderId) {
      this.snackbar.error('Choose a rider first');
      return;
    }

    this.isAssigningRider.set(true);

    const completeAssign = (distanceKm: number, estimatedMinutes: number) => {
      this.ordersService
        .assignRiderManually({
          orderId: order.order_id,
          riderId,
          distanceKm,
          estimatedMinutes,
        })
        .subscribe({
          next: () => {
            this.snackbar.success('Rider assigned manually');
            this.closeManualAssignment();
            this.loadOrders();
            const selected = this.selectedOrder();
            if (selected?.order_id === order.order_id) {
              this.selectedOrder.set({
                ...selected,
                rider_id: riderId as any,
                current_status: 'RIDER_ASSIGNED',
                status: 'rider_assigned',
              });
            }
            this.isAssigningRider.set(false);
          },
          error: (err) => {
            console.error('Manual rider assign failed:', err);
            this.snackbar.error('Failed to assign rider');
            this.isAssigningRider.set(false);
          },
        });
    };

    const route = this.ordersService.resolveOrderRoutePoints(order);
    if (route) {
      this.ordersService
        .getDistanceAndDuration(route.origin, route.destination)
        .subscribe({
          next: (res) =>
            completeAssign(
              res.distanceMeters / 1000,
              res.durationSeconds / 60
            ),
          error: () => {
            const fallback = this.ordersService.estimateRouteFallback(
              route.origin,
              route.destination
            );
            completeAssign(fallback.distanceKm, fallback.estimatedMinutes);
          },
        });
      return;
    }

    completeAssign(2, 15);
  }

  getPromotionTotal(order: AdminOrder): number {
    return (order.promotions || []).reduce(
      (sum, promotion) => sum + Number(promotion.discount_amount || 0),
      0
    );
  }

  getPaymentClass(status: string): string {
    switch (String(status || '').toUpperCase()) {
      case 'PAID':
        return 'badge-success';
      case 'FAILED':
      case 'REFUNDED':
        return 'badge-error';
      default:
        return 'badge-warning';
    }
  }

  private getActionLabel(status: OrderStatus): string {
    switch (status) {
      case 'PENDING':
        return 'Confirm order';
      case 'CONFIRMED':
        return 'Start packing';
      case 'PREPARING':
        return 'Mark ready & auto-assign rider';
      case 'READY_FOR_PICKUP':
        return 'Finding rider';
      case 'RIDER_ASSIGNED':
        return 'Rider heading to store';
      case 'RIDER_ACCEPTED':
        return 'Rider accepted — en route to store';
      case 'RIDER_AT_STORE':
        return 'Rider at store';
      case 'DISPATCHED':
        return 'In delivery';
      case 'RIDER_NEAR_CUSTOMER':
        return 'Rider nearby';
      case 'DELIVERED':
        return 'Completed';
      case 'CANCELLED':
      default:
        return 'No action';
    }
  }

  get totalPages(): number {
    return Math.ceil(this.totalOrders() / this.pageSize);
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadOrders();
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadOrders();
    }
  }

  getStatusClass(status: OrderStatus): string {
    switch (status) {
      case 'PENDING':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'CONFIRMED':
        return 'bg-info/10 text-info border-info/20';
      case 'PREPARING':
        return 'bg-secondary/10 text-secondary border-secondary/20';
      case 'READY_FOR_PICKUP':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'RIDER_ASSIGNED':
      case 'RIDER_ACCEPTED':
      case 'RIDER_AT_STORE':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'DISPATCHED':
      case 'RIDER_NEAR_CUSTOMER':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'DELIVERED':
        return 'bg-success/10 text-success border-success/20';
      case 'CANCELLED':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-base-300 text-base-content';
    }
  }

  private isNoAvailableRiderError(err: unknown): boolean {
    const message =
      err instanceof Error
        ? err.message
        : typeof err === 'string'
        ? err
        : (err as any)?.message || (err as any)?.details || '';

    return message.toLowerCase().includes('no available rider');
  }

  getActiveLinesCount(order: AdminOrder): number {
    return (order.items || []).filter(item => !item.is_fully_removed).length;
  }

  getFulfilledQty(order: AdminOrder): number {
    return (order.items || []).reduce((sum, item) => sum + (item.fulfilled_quantity || 0), 0);
  }
}
